import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import {
  ModalContent, Field, Label, Value, EditableInput,
  NotesInput, ButtonRow, HeaderRow, DateTimeRow,
  modalStyles, dateTimeInputStyles
} from '../../constants/leadModalStyles';
import EditIconButton from '../../components/common/EditIconButton';
import PrimaryButton from '../../components/common/PrimaryButton';
import SecondaryButton from '../../components/common/SecondaryButton';
import { FaEdit } from 'react-icons/fa';
import { LeadStatus } from '../../constants/leadStatus';
import LeadService from '../../services/LeadService';

// Add Google Fonts import for Inria Serif
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inria+Serif:wght@400;700&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);


const LeadInfoModal = ({ isOpen, onRequestClose, lead, onSave, onCancel }) => {

  const [formData, setFormData] = useState({
    notes: '',
    followupDate: '',
    followupTime: '',
    isEditing: false,
    editName: '',
    editCompany: '',
    editEmail: '',
    editPhone: ''
  });

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      notes: lead?.notes || '',
      editName: lead?.name || '',
      editCompany: lead?.company || '',
      editEmail: lead?.email || '',
      editPhone: lead?.phoneNumber || '',
      followupDate: '',
      followupTime: ''
    }));
    
    // Parse followupDate if present and status is Need Follow Up
    if (lead?.followupDate && lead?.followupDate !== '') {
      const match = lead.followupDate.match(/(\w+) (\d+) (\d{4}) at (\d{1,2}):(\d{2}) (AM|PM)/);
  
      const [, monthName, day, year, hour12, minute, ampm] = match;
        
        // Convert month name to number
        const monthIndex = new Date(Date.parse(monthName + " 1, 2000")).getMonth();
        const month = String(monthIndex + 1).padStart(2, '0');

        // Parse to Date object for the input fields (using 24-hour conversion)
        let hour24 = parseInt(hour12);
        if (ampm === 'PM' && hour24 !== 12) {
          hour24 += 12;
        } else if (ampm === 'AM' && hour24 === 12) {
          hour24 = 0;
        }

        const dateStr = `${year}-${month}-${day.padStart(2, '0')}`;

         // Create UTC date
        const utcDate = new Date(Date.UTC(
          parseInt(year),
          monthIndex,
          parseInt(day),
          hour24,
          parseInt(minute)
        ));

        // Convert to IST using timezone
        const istString = utcDate.toLocaleString('en-CA', {
          timeZone: 'Asia/Kolkata',
          hour12: false
        });

        // istString format → YYYY-MM-DD, HH:mm:ss
        const [datePart, timePart] = istString.split(', ');

        setFormData(prev => ({
          ...prev,
          followupDate: dateStr, 
          followupTime: timePart.slice(0, 5) 
        }));
    }
  }, [lead]);

  if (!lead) return null;

  const handleEdit = () => {
    setFormData(prev => ({
      ...prev,
      isEditing: true
    }));
  };

  const handleCancelEdit = () => {
    setFormData(prev => ({
      ...prev,
      isEditing: false,
      editName: lead.name,
      editCompany: lead.company,
      editEmail: lead.email,
      editPhone: lead.phoneNumber
    }));
    onCancel();
  };

  const handleSave = async () => {
    let updatedLead = { ...lead, notes: formData.notes };
    
    // If in edit mode, update name and phone
    if (formData.isEditing) {
      updatedLead.name = formData.editName;
      updatedLead.company = formData.editCompany;
      updatedLead.email = formData.editEmail;
      updatedLead.phoneNumber = formData.editPhone;
      setFormData(prev => ({
        ...prev,
        isEditing: false
      }));
    }
    
    if (formData.followupDate && formData.followupTime) {
      const dateObj = new Date(`${formData.followupDate}T${formData.followupTime}`);
      updatedLead.followupDate = dateObj.toISOString();
      updatedLead.contactOn = dateObj.toISOString();
    }

    const phoneNumber = String(formData.editPhone || lead.phoneNumber);
    const leadData = {
          leadId: updatedLead.leadId,
          firstName: updatedLead.name.split(' ')[0] || formData.name,
          lastName: updatedLead.name.split(' ').slice(1).join(' ') || 'N.A',
          whatsAppNo: phoneNumber.replace('91', ''),
          email: updatedLead.email,
          countryCode: '+91',
          company: updatedLead.company,
          leadsStatus: updatedLead.leadsStatus || LeadStatus.NEW_LEADS,
          notes: updatedLead.notes,
        };
    
        // Add follow-up date if provided
        if (formData.followupDate && formData.followupTime) {
          const dateObj = new Date(`${formData.followupDate}T${formData.followupTime}`);
          leadData.followupDate = dateObj.toISOString();
          leadData.contactOn = dateObj.toISOString();
        }

    // make the server call to update the lead
    try {
      const response = await LeadService.updateLead(leadData);
      if (response.data.success) {
        onSave(updatedLead);
        handleCancelEdit();
        onRequestClose();
      }
    } catch (error) {
      console.error('Error updating lead:', error);
    }
  };

  const customStyles = modalStyles;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Lead Info"
      style={customStyles}
    >
      <ModalContent>
      <HeaderRow>
       <h4 className="section-title mb-0">Lead Details</h4>
       <EditIconButton onClick={handleEdit} title="Edit Lead">
        <FaEdit />
    </EditIconButton>
</HeaderRow>

        <Field>
          <Label>Name:</Label>
          {formData.isEditing ? (
            <EditableInput
              type="text"
              value={formData.editName}
              onChange={(e) => setFormData(prev => ({ ...prev, editName: e.target.value }))}
            />
          ) : (
            <Value>{lead.name}</Value>
          )}
        </Field>
        
        <Field>
          <Label>Company:</Label>
          {formData.isEditing? (
            <EditableInput
              type="text"
              value={formData.editCompany}
              onChange={(e) => setFormData(prev => ({ ...prev, editCompany: e.target.value }))}
            />
          ) : (
            <Value>{lead.company}</Value>
          )}
        </Field>
        
        <Field>
          <Label>Email:</Label>
          {formData.isEditing ? (
            <EditableInput
              type="text"
              value={formData.editEmail}
              onChange={(e) => setFormData(prev => ({ ...prev, editEmail: e.target.value }))}
            />
          ) : (
            <Value>{lead.email}</Value>
          )}
        </Field>
        
        <Field>
          <Label>Phone:</Label>
          {formData.isEditing? (
            <EditableInput
              type="text"
              value={formData.editPhone}
              onChange={(e) => setFormData(prev => ({ ...prev, editPhone: e.target.value }))}
            />
          ) : (
            <Value>{lead.phoneNumber}</Value>
          )}
        </Field>
        
        <DateTimeRow>
            <Label style={{ marginBottom: 0 }}>Follow-up On:</Label>
            <input
              type="date"
              value={formData.followupDate}
              onChange={e => setFormData(prev => ({ ...prev, followupDate: e.target.value }))}
              style={dateTimeInputStyles}
            />
            <input
              type="time"
              value={formData.followupTime}
              onChange={e => setFormData(prev => ({ ...prev, followupTime: e.target.value }))}
              style={dateTimeInputStyles}
            />
          </DateTimeRow>
        <Field>
          <Label>Notes:</Label>
          <NotesInput
            value={formData.notes}
            onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Add notes here..."
          />
        </Field>
        <ButtonRow>
          <SecondaryButton onClick={handleCancelEdit}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSave}>Save</PrimaryButton>
        </ButtonRow>
      </ModalContent>
    </Modal>
  );
};

export default LeadInfoModal; 